/**
 * Dictation in the browser: the composer's mic, which writes the words live in
 * the box and never sends them.
 *
 * The phone's `@/hooks/use-dictation` over the phone's pure machine
 * (`@/lib/dictation-state`), with the browser's microphone (`microphone.ts`)
 * and socket (`dictation-socket.ts`) in place of `react-native-audio-api` and
 * React Native's headered socket. Two paths, as on the phone: on a server
 * with a live transcriber (the status's Google key, `dictationLive`) the words
 * stream back over `/v1/agent/dictate/live` while the person speaks; on one
 * without, or when the socket does not open within `DICTATION_OPEN_MS`, the
 * recording is uploaded when it stops (`/v1/agent/transcribe`, as a WAV). The
 * recording is kept on both paths, so a slow open loses nothing.
 *
 *   const level = useVoiceLevel();
 *   const dictation = useDictation({ tenantId, level, onText: (text) => setDraft(join(draft, text)) });
 *
 *   - `start()` on the mic. `status` is `recording` while it listens, with
 *     `heard` the words so far on the live path and `startedAt` the moment it
 *     began, for the box's timer.
 *   - `finish()` is the check: the settled words go to `onText`, to stand in
 *     the box for the person to read and send (the web's check never sends,
 *     where the phone's does). `settling` (live) and `transcribing` (upload)
 *     are the moment between; a second check does nothing.
 *   - At sixty seconds the recording stops and its words are `held`, waiting
 *     for the check.
 *   - `nothing_heard` is a dictation whose words came back empty; `mic_off` a
 *     refused microphone, `no_mic` a browser with none, `failed` a
 *     microphone, socket or transcriber that failed (words heard before a drop
 *     still go to `onText`). Each is left by the next `start` or a `cancel`.
 *   - `cancel()` is the cross: the recording and its words are dropped.
 *   - Unmounting cancels.
 */
import { DEFAULT_AGENT_LANGUAGE, LIVE_INPUT_RATE, type AgentLanguageTag } from '@alluzo/shared';
import { useCallback, useEffect, useRef, useState } from 'react';

import { transcribeSetupVoice } from '@/lib/agent-api';
import { dictationLive } from '@/lib/agent-status';
import { ApiRequestError } from '@/lib/api';
import {
  DICTATION_IDLE,
  dictationStep,
  uploadRefusal,
  type DictationEffect,
  type DictationEvent,
  type DictationPath,
  type DictationState,
  type DictationStatus as MachineStatus,
} from '@/lib/dictation-state';
import { onMicQueue } from '@/lib/mic-queue';
import { hushLevel, writeLevelSteps } from '@/lib/orb-level';
import { blockLevels, createRoomFloor, levelOf } from '@/lib/voice-level';
import { pcm16Wav } from '@/lib/wav';
import { useAgentStatusStore } from '@/stores/agent-status-store';

import { connectDictation, type DictationSocket } from './dictation-socket';
import type { VoiceLevel } from './level';
import { micFailureOf, openMicrophone, type Microphone } from './microphone';

/** The phone's statuses, and `no_mic`: a desktop can have no microphone at all. */
export type DictationStatus = MachineStatus | 'no_mic';

export type Dictation = {
  readonly status: DictationStatus;
  readonly path: DictationPath;
  /** The words so far on the live path, or the held words at the cap; null while there are none. */
  readonly heard: string | null;
  /** When the recording began (ms since the epoch), while it records; the box's timer counts from it. */
  readonly startedAt: number | null;
  /** Open the mic. Does nothing while a dictation is under way. */
  readonly start: () => void;
  /** The check: the words go to `onText`, into the box. */
  readonly finish: () => void;
  /** The cross: the recording and its words are dropped; nothing reaches `onText`. Also clears a leftover status. */
  readonly cancel: () => void;
};

export type DictationInput = {
  /** The business the live socket is opened inside. Without one, the recording is uploaded. */
  readonly tenantId?: string | null;
  /** Where the recording's loudness is written, 0..1. */
  readonly level: VoiceLevel;
  /** The words, trimmed and never empty. */
  readonly onText: (text: string) => void;
  /** The transcriber's hint, so a Hindi sentence is heard as Hindi. Absent is English. */
  readonly language?: AgentLanguageTag;
};

/** The longest one dictation runs: a minute is a paragraph, and the upload stays small (about 1.9 MB). */
export const DICTATION_MAX_MS = 60_000;
const MAX_BYTES = (DICTATION_MAX_MS / 1000) * LIVE_INPUT_RATE * 2;

/** How long the socket has to say `open` before the recording is uploaded instead. The phone's. A timeout, not a motion. */
export const DICTATION_OPEN_MS = 5_000;
/** After the check, how long the socket has to answer `done` before the words heard so far are taken. The phone's. */
export const DICTATION_SETTLE_BACKSTOP_MS = 2_500;

export const useDictation = (input: DictationInput): Dictation => {
  const called = useRef(input);
  useEffect(() => {
    called.current = input;
  });

  const machine = useRef<DictationState>(DICTATION_IDLE);
  const [shown, setShown] = useState<DictationState>(DICTATION_IDLE);
  /** The refused microphone was no microphone at all: the machine knows only `mic_off`. */
  const [noMic, setNoMic] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  /** Which dictation is current: a cancel or a new start makes everything older stale. */
  const era = useRef(0);
  /** Frames are being kept: from the microphone opening until the recording stops. */
  const recording = useRef(false);
  const microphone = useRef<Microphone | null>(null);
  const socket = useRef<DictationSocket | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chunks = useRef<Uint8Array[]>([]);
  const bytes = useRef(0);
  const room = useRef(createRoomFloor());

  /**
   * Stop the microphone on the one queue, behind whatever is opening or
   * closing it. `flush` keeps the last partial frame: the last word.
   */
  const release = useCallback((flush: boolean): Promise<void> => {
    hushLevel(called.current.level);
    return onMicQueue(async () => {
      const held = microphone.current;
      microphone.current = null;
      if (held !== null) await held.stop({ flush });
    });
  }, []);

  const clearOpenTimer = (): void => {
    if (openTimer.current !== null) clearTimeout(openTimer.current);
    openTimer.current = null;
  };

  const closeSocket = (): void => {
    clearOpenTimer();
    socket.current?.close();
    socket.current = null;
  };

  /** One event through the machine for the dictation `mine`: drawn, and its effects run. */
  function dispatch(mine: number, event: DictationEvent): void {
    if (era.current !== mine) return;
    const next = dictationStep(machine.current, event);
    if (next.state === machine.current) return;
    machine.current = next.state;
    if (next.state.status !== 'settling' && settleTimer.current !== null) {
      clearTimeout(settleTimer.current);
      settleTimer.current = null;
    }
    setShown(next.state);
    if (next.state.status !== 'recording') setStartedAt(null);
    for (const effect of next.effects) run(mine, effect);
  }

  function run(mine: number, effect: DictationEffect): void {
    switch (effect.type) {
      case 'stop_recorder':
        // The last partial frame is kept (the flush) before the frames stop being taken.
        void release(true).finally(() => {
          if (era.current === mine) recording.current = false;
        });
        return;
      case 'finish_socket':
        socket.current?.finish();
        return;
      case 'close_socket':
        closeSocket();
        return;
      case 'cancel_socket':
        clearOpenTimer();
        socket.current?.cancel();
        socket.current = null;
        return;
      case 'settle_timer':
        if (settleTimer.current !== null) clearTimeout(settleTimer.current);
        settleTimer.current = setTimeout(() => {
          settleTimer.current = null;
          dispatch(mine, { type: 'settled', text: machine.current.heard });
        }, DICTATION_SETTLE_BACKSTOP_MS);
        return;
      case 'upload':
        void upload(mine);
        return;
      case 'hand': {
        // Every intent lands in the box on the web: the check puts the words there to read, a drop keeps them.
        const text = effect.text.trim();
        if (text !== '') called.current.onText(text);
        return;
      }
    }
  }

  /** The recording, as a WAV, to the transcriber that hears a whole one. */
  async function upload(mine: number): Promise<void> {
    // Behind the stop that flushes the last frame.
    await onMicQueue(async () => undefined);
    if (era.current !== mine) return;
    const recorded = chunks.current;
    chunks.current = [];
    bytes.current = 0;
    if (recorded.length === 0) {
      dispatch(mine, { type: 'nothing' });
      return;
    }
    try {
      const text = await transcribeSetupVoice('', pcm16Wav(recorded, LIVE_INPUT_RATE), called.current.language);
      dispatch(mine, { type: 'settled', text });
    } catch (error) {
      // Only the server's no-speech code is a quiet room; any other refusal is a failed upload.
      dispatch(mine, uploadRefusal(error instanceof ApiRequestError ? error.code : null));
    }
  }

  const start = useCallback(() => {
    const status = machine.current.status;
    if (status === 'recording' || status === 'settling' || status === 'transcribing' || status === 'held') return;
    const tenantId = called.current.tenantId ?? null;
    const path: DictationPath = tenantId !== null && dictationLive(useAgentStatusStore.getState().held) ? 'live' : 'upload';
    const mine = era.current + 1;
    era.current = mine;
    recording.current = false;
    chunks.current = [];
    bytes.current = 0;
    room.current.reset();
    closeSocket();
    setNoMic(false);
    dispatch(mine, { type: 'start', path });
    setStartedAt(Date.now());

    if (path === 'live' && tenantId !== null) {
      socket.current = connectDictation(tenantId, called.current.language ?? DEFAULT_AGENT_LANGUAGE, {
        onMessage: (message) => {
          switch (message.type) {
            case 'open':
              clearOpenTimer();
              dispatch(mine, { type: 'open' });
              return;
            case 'words':
              dispatch(mine, { type: 'words', text: message.text });
              return;
            case 'done':
              dispatch(mine, { type: 'settled', text: message.text });
              return;
            case 'error':
              dispatch(mine, { type: 'lost' });
              return;
          }
        },
        // A close this hook asked for takes its handlers off first, so any close here is a loss.
        onClose: () => dispatch(mine, { type: 'lost' }),
        onError: () => dispatch(mine, { type: 'lost' }),
      });
      // No `open` in time: the recording goes on, and is uploaded when it stops.
      openTimer.current = setTimeout(() => {
        openTimer.current = null;
        dispatch(mine, { type: 'lost' });
      }, DICTATION_OPEN_MS);
    }

    void onMicQueue(async () => {
      if (era.current !== mine) return;
      const opened = await openMicrophone((pcm) => {
        if (era.current !== mine || !recording.current) return;
        // Kept on every path — the upload's material if the socket never opens — and streamed on the live one.
        chunks.current.push(pcm);
        bytes.current += pcm.length;
        socket.current?.sendAudio(pcm);
        writeLevelSteps(
          called.current.level,
          blockLevels(pcm, LIVE_INPUT_RATE).map((block) => ({ value: levelOf(block.db, room.current.feed(block.db)), ms: block.ms })),
        );
        // The cap holds the words for the check; it never sends them.
        if (bytes.current >= MAX_BYTES) dispatch(mine, { type: 'cap' });
      });
      microphone.current = opened;
      // A cancel or a check that came while the mic opened has queued its
      // release behind this; the frames are kept only while still recording.
      if (era.current === mine && machine.current.status === 'recording') recording.current = true;
    }).catch((caught: unknown) => {
      if (era.current !== mine) return;
      recording.current = false;
      chunks.current = [];
      bytes.current = 0;
      void release(false);
      const failure = micFailureOf(caught);
      if (failure === 'failed') {
        dispatch(mine, { type: 'recorder_failed' });
        return;
      }
      setNoMic(failure === 'no_mic');
      dispatch(mine, { type: 'mic_refused' });
    });
    // `dispatch`, `closeSocket` and `clearOpenTimer` read only refs and setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [release]);

  // `dispatch` reads only refs and setters, so a first render's is as good as the last.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const finish = useCallback(() => dispatch(era.current, { type: 'send' }), []);

  const cancel = useCallback(() => {
    const mine = era.current;
    dispatch(mine, { type: 'cancel' });
    // Whatever is still in flight for this dictation is stale from here.
    era.current = mine + 1;
    recording.current = false;
    chunks.current = [];
    bytes.current = 0;
    closeSocket();
    setNoMic(false);
    setStartedAt(null);
    // A status the last dictation left (nothing heard, a refusal) goes with the cancel.
    if (machine.current.status !== 'idle') {
      machine.current = DICTATION_IDLE;
      setShown(DICTATION_IDLE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Leaving drops a recording nobody will send.
  useEffect(() => () => cancel(), [cancel]);

  const status: DictationStatus = shown.status === 'mic_off' && noMic ? 'no_mic' : shown.status;
  return { status, path: shown.path, heard: shown.heard === '' ? null : shown.heard, startedAt, start, finish, cancel };
};
